import React, { useState } from 'react';
import { usePreferences } from '../contexts/PreferencesContext';

interface InputFieldProps {
  label: string;
  name: string;
  value: number | '';
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  type?: string;
  min?: string;
  max?: string;
  step?: string;
  unit?: string;
  description?: string;
}

interface ExtendedInputFieldProps extends InputFieldProps {
  defaultValue?: number | '';
  showDefaultIndicator?: boolean;
}

const InputField: React.FC<ExtendedInputFieldProps> = ({ 
  label, name, value, onChange, error, type = 'number', min, max, step, unit, description, defaultValue, showDefaultIndicator = false 
}) => {
  const isDifferentFromDefault = showDefaultIndicator && defaultValue !== undefined && value !== defaultValue && value !== '';
  
  return (
    <div className="mb-3 flex flex-col">
      <label htmlFor={name} className="block text-sm font-medium text-slate-700 mb-1 min-h-[40px] flex items-start">
        <span>{label}</span>
        {isDifferentFromDefault && (
          <span className="ml-2 relative group inline-block">
            <span className="text-blue-600 font-bold cursor-help">•</span>
            <div className="absolute z-50 invisible group-hover:visible bg-slate-800 text-white text-xs rounded-md py-1 px-2 bottom-full mb-1 left-1/2 -translate-x-1/2 whitespace-nowrap shadow-lg">
              <div className="absolute bottom-[-4px] left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-slate-800"></div>
              Default: {defaultValue}{unit ? ` ${unit}` : ''}
            </div>
          </span>
        )}
      </label>
    <div className="relative">
      <input
        type={type}
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        min={min}
        max={max}
        step={step}
        title={description}
        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
          error ? 'border-red-500' : 'border-slate-300'
        } ${unit ? 'pr-12' : ''}`}
        aria-describedby={error ? `${name}-error` : undefined}
      />
      {unit && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">
          {unit}
        </span>
      )}
    </div>
    {error && <p id={`${name}-error`} className="mt-1 text-xs text-red-600">{error}</p>}
  </div>
  );
};

interface ArmTimingData {
  stationNumber: number;
  armOpenDelay: number | '';
  armOpenDuration: number | '';
}

interface InfeedSettingsData {
  conveyorStartDelay: number | '';
  conveyorStopDelay: number | '';
  bufferHopperOpenDelay: number | '';
  bufferHopperCloseDelay: number | '';
  weighingDelay: number | '';
  zeroingDelay: number | '';
  zeroThresholdWeight: number | '';
  hopperCapacity: number | '';
  defaultInfeedTargetWeight: number | '';
  armTimingSettings?: ArmTimingData[];
}

interface InfeedSettingsV2Props {
  infeedData: InfeedSettingsData;
  onChange: (field: keyof InfeedSettingsData, value: number | '' | ArmTimingData[]) => void;
  errors?: Partial<Record<keyof InfeedSettingsData | string, string>>;
  numberOfStations?: number;
  systemDefaults?: Partial<InfeedSettingsData>;
  showDefaultIndicators?: boolean;
}

type InfeedSubTab = 'weighHopper' | 'armTiming';

const InfeedSettingsV2: React.FC<InfeedSettingsV2Props> = ({ 
  infeedData, 
  onChange, 
  errors = {},
  numberOfStations = 10,
  systemDefaults = {},
  showDefaultIndicators = false
}) => {
  const [activeSubTab, setActiveSubTab] = useState<InfeedSubTab>('weighHopper');
  const { weightUnit } = usePreferences();
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numValue = value === '' ? '' : parseFloat(value);
    onChange(name as keyof InfeedSettingsData, numValue);
  };

  const handleArmTimingChange = (stationIndex: number, field: 'armOpenDelay' | 'armOpenDuration', value: string) => {
    const numValue = value === '' ? '' : parseFloat(value);
    const currentSettings = infeedData.armTimingSettings || [];
    
    // Ensure we have settings for all stations
    const newSettings = [...Array(numberOfStations)].map((_, index) => {
      const existing = currentSettings[index] || {
        stationNumber: index + 1,
        armOpenDelay: 100,
        armOpenDuration: 500
      };
      
      if (index === stationIndex) {
        return {
          ...existing,
          [field]: numValue
        };
      }
      return existing;
    });
    
    onChange('armTimingSettings', newSettings);
  };
  
  // Helper function to get default arm timing value
  const getDefaultArmTimingValue = (stationIndex: number, field: 'armOpenDelay' | 'armOpenDuration'): number => {
    const defaultSettings = systemDefaults.armTimingSettings as ArmTimingData[] | undefined;
    if (defaultSettings && defaultSettings[stationIndex]) {
      return defaultSettings[stationIndex][field] as number;
    }
    return field === 'armOpenDelay' ? 100 : 500;
  };

  // Initialize arm timing settings if not present
  React.useEffect(() => {
    if (!infeedData.armTimingSettings || infeedData.armTimingSettings.length !== numberOfStations) {
      const defaultSettings = [...Array(numberOfStations)].map((_, index) => ({
        stationNumber: index + 1,
        armOpenDelay: 100,
        armOpenDuration: 500
      }));
      onChange('armTimingSettings', defaultSettings);
    }
  }, [numberOfStations]);

  return (
    <div className="space-y-4">
      {/* Sub-tabs for Weigh Hopper / Arm Timing */}
      <div className="border-b border-slate-200">
        <nav className="-mb-px flex space-x-4" aria-label="Infeed Settings Sub-tabs">
          <button
            type="button"
            onClick={() => setActiveSubTab('weighHopper')}
            className={`py-2 px-3 text-sm font-medium border-b-2 transition-colors ${
              activeSubTab === 'weighHopper' 
                ? 'border-indigo-500 text-indigo-600' 
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            Weigh Hopper
          </button>
          <button
            type="button"
            onClick={() => setActiveSubTab('armTiming')}
            className={`py-2 px-3 text-sm font-medium border-b-2 transition-colors ${
              activeSubTab === 'armTiming' 
                ? 'border-indigo-500 text-indigo-600' 
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            Arm Timing
          </button>
        </nav>
      </div>

      {/* Sub-tab Content */}
      {activeSubTab === 'weighHopper' ? (
        <div className="space-y-3">
          <div className="border-b border-slate-200 pb-2 mb-3">
            <h3 className="text-lg font-medium text-slate-900">Weigh Hopper Settings</h3>
            <p className="text-sm text-slate-500">Configure timing and weight parameters for the weigh hopper system</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <InputField
              label="Conveyor Start Delay"
              name="conveyorStartDelay"
              value={infeedData.conveyorStartDelay}
              onChange={handleChange}
              error={errors.conveyorStartDelay}
              min="0"
              max="30000"
              unit="ms"
              description="Time to wait before starting the conveyor when cycle begins"
              defaultValue={systemDefaults.conveyorStartDelay}
              showDefaultIndicator={showDefaultIndicators}
            />

            <InputField
              label="Conveyor Stop Delay"
              name="conveyorStopDelay"
              value={infeedData.conveyorStopDelay}
              onChange={handleChange}
              error={errors.conveyorStopDelay}
              min="0"
              max="30000"
              unit="ms"
              description="Time to wait before stopping the conveyor"
              defaultValue={systemDefaults.conveyorStopDelay}
              showDefaultIndicator={showDefaultIndicators}
            />

            <InputField
              label="Buffer Hopper Open Delay"
              name="bufferHopperOpenDelay"
              value={infeedData.bufferHopperOpenDelay}
              onChange={handleChange}
              error={errors.bufferHopperOpenDelay}
              min="0"
              max="10000"
              unit="ms"
              description="Delay before opening the buffer hopper gate"
              defaultValue={systemDefaults.bufferHopperOpenDelay}
              showDefaultIndicator={showDefaultIndicators}
            />

            <InputField
              label="Buffer Hopper Close Delay"
              name="bufferHopperCloseDelay"
              value={infeedData.bufferHopperCloseDelay}
              onChange={handleChange}
              error={errors.bufferHopperCloseDelay}
              min="0"
              max="10000"
              unit="ms"
              description="Delay before closing the buffer hopper gate"
              defaultValue={systemDefaults.bufferHopperCloseDelay}
              showDefaultIndicator={showDefaultIndicators}
            />

            <InputField
              label="Weighing Delay"
              name="weighingDelay"
              value={infeedData.weighingDelay}
              onChange={handleChange}
              error={errors.weighingDelay}
              min="0"
              max="60000"
              unit="ms"
              description="Time to wait for scale to stabilize before reading weight"
              defaultValue={systemDefaults.weighingDelay}
              showDefaultIndicator={showDefaultIndicators}
            />

            <InputField
              label="Zeroing Delay"
              name="zeroingDelay"
              value={infeedData.zeroingDelay}
              onChange={handleChange}
              error={errors.zeroingDelay}
              min="0"
              max="10000"
              unit="ms"
              description="Time to wait during scale zeroing process"
              defaultValue={systemDefaults.zeroingDelay}
              showDefaultIndicator={showDefaultIndicators}
            />

            <InputField
              label="Zero Threshold Weight"
              name="zeroThresholdWeight"
              value={infeedData.zeroThresholdWeight}
              onChange={handleChange}
              error={errors.zeroThresholdWeight}
              min="0"
              max="10"
              step="0.01"
              unit={weightUnit === 'KGS' ? 'KG' : 'LB'}
              description="Minimum weight to consider as material present (not empty)"
              defaultValue={systemDefaults.zeroThresholdWeight}
              showDefaultIndicator={showDefaultIndicators}
            />

            <InputField
              label="Hopper Capacity"
              name="hopperCapacity"
              value={infeedData.hopperCapacity}
              onChange={handleChange}
              error={errors.hopperCapacity}
              min="1"
              max="1000"
              step="0.1"
              unit={weightUnit === 'KGS' ? 'KG' : 'LB'}
              description="Maximum weight capacity of the hopper (safety limit)"
              defaultValue={systemDefaults.hopperCapacity}
              showDefaultIndicator={showDefaultIndicators}
            />

            <InputField
              label="Target Weight"
              name="defaultInfeedTargetWeight"
              value={infeedData.defaultInfeedTargetWeight}
              onChange={handleChange}
              error={errors.defaultInfeedTargetWeight}
              min="0.1"
              max="100"
              step="0.1"
              unit={weightUnit === 'KGS' ? 'KG' : 'LB'}
              description="Default target weight when product-specific weights aren't set"
              defaultValue={systemDefaults.defaultInfeedTargetWeight}
              showDefaultIndicator={showDefaultIndicators}
            />
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="border-b border-slate-200 pb-2 mb-3">
            <h3 className="text-lg font-medium text-slate-900">Arm Timing Settings</h3>
            <p className="text-sm text-slate-500">Configure arm open delay and duration for each station ({numberOfStations} stations)</p>
          </div>

          <div className="space-y-4">
            {/* Header Row */}
            <div className="grid grid-cols-3 gap-4 pb-2 border-b border-slate-200">
              <div className="text-sm font-semibold text-slate-700">Station</div>
              <div className="text-sm font-semibold text-slate-700">Arm Open Delay</div>
              <div className="text-sm font-semibold text-slate-700">Arm Open Duration</div>
            </div>

            {/* Station Rows */}
            <div className="max-h-[400px] overflow-y-auto space-y-2">
              {[...Array(numberOfStations)].map((_, index) => {
                const stationSettings = infeedData.armTimingSettings?.[index] || {
                  stationNumber: index + 1,
                  armOpenDelay: 100,
                  armOpenDuration: 500
                };
                
                const defaultDelay = getDefaultArmTimingValue(index, 'armOpenDelay');
                const defaultDuration = getDefaultArmTimingValue(index, 'armOpenDuration');
                const isDelayDifferent = showDefaultIndicators && stationSettings.armOpenDelay !== defaultDelay && stationSettings.armOpenDelay !== '';
                const isDurationDifferent = showDefaultIndicators && stationSettings.armOpenDuration !== defaultDuration && stationSettings.armOpenDuration !== '';
                const hasAnyDifference = isDelayDifferent || isDurationDifferent;
                
                return (
                  <div key={index} className="grid grid-cols-3 gap-4 items-center p-2 hover:bg-slate-50 rounded">
                    <div className="text-sm font-medium text-slate-700">
                      Station {index + 1}
                      {hasAnyDifference && (
                        <span className="ml-2 relative group inline-block">
                          <span className="text-blue-600 font-bold cursor-help">•</span>
                          <div className="absolute z-50 invisible group-hover:visible bg-slate-800 text-white text-xs rounded-md py-2 px-3 bottom-full mb-1 left-1/2 -translate-x-1/2 whitespace-nowrap shadow-lg">
                            <div className="absolute bottom-[-4px] left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-slate-800"></div>
                            <div className="space-y-1">
                              {isDelayDifferent && (
                                <div>Open Delay: Default {defaultDelay}ms</div>
                              )}
                              {isDurationDifferent && (
                                <div>Open Duration: Default {defaultDuration}ms</div>
                              )}
                            </div>
                          </div>
                        </span>
                      )}
                    </div>
                    <div>
                      <div className="relative">
                        <input
                          type="number"
                          value={stationSettings.armOpenDelay}
                          onChange={(e) => handleArmTimingChange(index, 'armOpenDelay', e.target.value)}
                          min="0"
                          max="10000"
                          className={`w-full px-3 py-1.5 pr-12 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                            errors[`armOpenDelay_${index}`] ? 'border-red-500' : 'border-slate-300'
                          }`}
                          placeholder="100"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">
                          ms
                        </span>
                      </div>
                      {errors[`armOpenDelay_${index}`] && (
                        <p className="mt-1 text-xs text-red-600">{errors[`armOpenDelay_${index}`]}</p>
                      )}
                    </div>
                    <div>
                      <div className="relative">
                        <input
                          type="number"
                          value={stationSettings.armOpenDuration}
                          onChange={(e) => handleArmTimingChange(index, 'armOpenDuration', e.target.value)}
                          min="0"
                          max="10000"
                          className={`w-full px-3 py-1.5 pr-12 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                            errors[`armOpenDuration_${index}`] ? 'border-red-500' : 'border-slate-300'
                          }`}
                          placeholder="500"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">
                          ms
                        </span>
                      </div>
                      {errors[`armOpenDuration_${index}`] && (
                        <p className="mt-1 text-xs text-red-600">{errors[`armOpenDuration_${index}`]}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="mt-4 p-3 bg-blue-50 rounded-md">
              <p className="text-sm text-blue-800">
                <strong>Arm Open Delay:</strong> Time to wait before opening the station arm (milliseconds)
              </p>
              <p className="text-sm text-blue-800 mt-1">
                <strong>Arm Open Duration:</strong> How long the station arm stays open (milliseconds)
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InfeedSettingsV2;